import { useEffect } from 'react';

interface MetaTags {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
}

let baseNameDescription: string | undefined;
let baseOgDescription: string | undefined;
let isBaseDescriptionCaptured = false;

export const useMetaTags = (metaTags: MetaTags) => {
  useEffect(() => {
    const baseUrl = window.location.origin;
    const currentUrl = baseUrl + window.location.pathname;
    
    // 기본 og tag 설정
    // 페이지가 description을 넘기지 못하는 경우, 기존 index.html에 있던 기본값으로 되돌리기 위해
    // 초기 base description을 한 번 캡처해 둡니다.
    if (!isBaseDescriptionCaptured) {
      baseNameDescription = document
        .querySelector('meta[name="description"]')
        ?.getAttribute('content') ?? undefined;
      baseOgDescription = document
        .querySelector('meta[property="og:description"]')
        ?.getAttribute('content') ?? undefined;
      isBaseDescriptionCaptured = true;
    }

    const pageDescription = metaTags.description?.trim();
    const hasPageDescription = Boolean(pageDescription);

    const normalizeImageUrl = (rawImage?: string): string => {
      const img = rawImage?.trim();
      if (!img) return `${baseUrl}/ogimage.png`;
      if (/^https?:\/\//i.test(img)) return img;
      if (img.startsWith('/')) return `${baseUrl}${img}`;
      return `${baseUrl}/${img}`;
    };

    const normalizeTitle = (rawTitle?: string): string => {
      const base = 'Lynkim';
      const t = rawTitle?.trim();
      if (!t) return base;
      if (t.toLowerCase() === base.toLowerCase()) return base;

      // 기존 코드에서 흔히 쓰는 형태:
      // 1) "{page} - Lynkim"
      // 2) "Lynkim - {page}"
      // 위 두 케이스를 모두 "{page} - Lynkim"으로 정규화합니다.
      const startsWithBase = t.match(/^lynkim\s*-\s*(.+)$/i);
      if (startsWithBase?.[1]?.trim()) {
        return `${startsWithBase[1].trim()} - ${base}`;
      }

      const endsWithBase = t.match(/^(.+?)\s*-\s*lynkim\s*$/i);
      if (endsWithBase?.[1]?.trim()) {
        return `${endsWithBase[1].trim()} - ${base}`;
      }

      // 그 외 형태는 "{page}"로 취급
      return `${t} - ${base}`;
    };

    const defaultTags = {
      title: normalizeTitle(metaTags.title),
      image: normalizeImageUrl(metaTags.image),
      url: metaTags.url || currentUrl,
      type: metaTags.type || 'website',
      siteName: metaTags.siteName || 'Lynkim',
    };

    // 기존 meta tag 제거 함수
    const removeMetaTag = (property: string) => {
      const existingTag = document.querySelector(`meta[property="${property}"]`);
      if (existingTag) {
        existingTag.remove();
      }
    };

    // Title 설정
    if (defaultTags.title) {
      document.title = defaultTags.title;
    }

    // Meta tags 설정
    const setMetaTag = (property: string, content: string) => {
      removeMetaTag(property);
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    };

    // Description meta tag
    const setDescriptionMeta = (content: string) => {
      const existingDesc = document.querySelector('meta[name="description"]');
      if (existingDesc) {
        existingDesc.setAttribute('content', content);
      } else {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    };

    // OG tags 설정
    setMetaTag('og:title', defaultTags.title);
    if (hasPageDescription) {
      setMetaTag('og:description', pageDescription as string);
    } else if (baseOgDescription) {
      setMetaTag('og:description', baseOgDescription);
    }
    setMetaTag('og:image', defaultTags.image);
    setMetaTag('og:url', defaultTags.url);
    setMetaTag('og:type', defaultTags.type);
    setMetaTag('og:site_name', defaultTags.siteName);
    setMetaTag('og:locale', 'en_US');
    setMetaTag('og:image:width', '1200');
    setMetaTag('og:image:height', '630');
    
    // Meta Description meta tag
    if (hasPageDescription) {
      setDescriptionMeta(pageDescription as string);
    } else if (baseNameDescription) {
      setDescriptionMeta(baseNameDescription);
    }

    // Cleanup function
    return () => {
      // 컴포넌트 언마운트 시 기본값으로 복원하지 않음 (다음 페이지가 설정할 것)
    };
  }, [metaTags]);
};

